import { Add } from '@mui/icons-material'
import { Chip, IconButton, Stack, TextField, useTheme } from '@mui/material'
import { useFormikContext } from 'formik'
import { useState } from 'react'

import {
  ECampaignTemplateFormKeys,
  TCreateCampaignTemplateForm,
  TCreateTemplateFromCampaignForm,
} from '~/features/admin/models'
import rem from '~/utils/ui'

export type TProps = {
  isLoading?: boolean
}

export default function CampaignTemplatesVerticalTags({
  isLoading = false,
}: TProps) {
  const formik = useFormikContext<
    TCreateCampaignTemplateForm | TCreateTemplateFromCampaignForm
  >()
  const [verticalTagValue, setVerticalTagValue] = useState<string>('')
  const { values, setFieldValue } = formik
  const theme = useTheme()
  const handleVerticalTagChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setVerticalTagValue(event.target.value)
  }

  const handleAddVerticalTag = () => {
    if (!verticalTagValue.trim()) return

    const newVerticalTags = [
      ...(values[ECampaignTemplateFormKeys.verticalTags] || []),
      verticalTagValue,
    ]
    setFieldValue(ECampaignTemplateFormKeys.verticalTags, newVerticalTags)
    setVerticalTagValue('')
  }

  const handleVerticalTagKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === 'Enter') {
      handleAddVerticalTag()
    }
  }

  const handleDeleteVerticalTag = (verticalTag: string) => {
    const newVerticalTags =
      values[ECampaignTemplateFormKeys.verticalTags]?.filter(
        (tag) => tag !== verticalTag,
      ) || []
    setFieldValue(ECampaignTemplateFormKeys.verticalTags, newVerticalTags)
  }

  return (
    <Stack gap={1}>
      <Stack direction="row" gap={1}>
        <TextField
          size="small"
          label="Vertical Tags"
          value={verticalTagValue}
          onChange={handleVerticalTagChange}
          placeholder="Enter a vertical tag"
          fullWidth
          onKeyDown={handleVerticalTagKeyDown}
          disabled={isLoading}
        />
        <IconButton
          onClick={handleAddVerticalTag}
          disabled={isLoading}
          sx={{
            borderRadius: rem(8),
            backgroundColor: theme.palette.secondary2,
            color: theme.palette.common.white,
            '&:hover': { color: theme.palette.text.primary },
          }}
        >
          <Add />
        </IconButton>
      </Stack>

      <Stack direction="row" gap={1} flexWrap="wrap">
        {values[ECampaignTemplateFormKeys.verticalTags]?.map((verticalTag) => (
          <Chip
            key={verticalTag}
            label={verticalTag}
            onDelete={() => handleDeleteVerticalTag(verticalTag)}
            disabled={isLoading}
            sx={{ borderRadius: rem(6) }}
            size="small"
          />
        ))}
      </Stack>
    </Stack>
  )
}
